/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, ReactNode, useEffect, useCallback, useState } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Autocomplete, AutocompleteRenderInputParams, TextField, Box, Grid } from '@mui/material';
import debounce from 'lodash.debounce';
import { useGetFullAddressQuery } from '@services';
import { LoqateFullAddress } from '@models';
import { LoqateInitialState } from '@constants';

type Props = {
  onSelectedAddress: (selectedAddress: Partial<LoqateFullAddress>) => void;
  isReset: boolean;
};

export const LoqateAddressFinder: FC<Props> = ({ onSelectedAddress, isReset }: Props) => {
  const [addressValue, setAddressValue] = useState<LoqateFullAddress>(LoqateInitialState);
  const [options, setOptions] = useState<LoqateFullAddress[]>([]);
  const [newAddressValue, setNewAddressValue] = useState<string>('');
  const [textValue, setTextValue] = useState<string>('');

  // Call the API when newValue is set
  const { data } = useGetFullAddressQuery({ address: newAddressValue }, { skip: !newAddressValue });

  const debounceFn = useCallback(debounce(handleDebounceFn, 500), []);

  function handleDebounceFn(inputValue: string) {
    setNewAddressValue(inputValue);
  }

  useEffect(() => {
    if (data?.length) {
      setOptions(data);
    }
  }, [data]);

  useEffect(() => {
    setTextValue('');
    setNewAddressValue('');
    setOptions([]);

    // Reset the data once the item is saved
    setAddressValue(LoqateInitialState);
  }, [isReset]);

  return (
    <Box pb={5}>
      <Autocomplete
        data-testid="loqate-address-finder"
        getOptionLabel={(option) => option.fullAddress}
        isOptionEqualToValue={(option, value) => option.fullAddress === value.fullAddress}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={addressValue}
        key={isReset.toString()}
        onChange={(event, newValue) => {
          setAddressValue(newValue!);
          onSelectedAddress(newValue!);
          setTextValue('');
        }}
        onInputChange={(event, newInputValue) => {
          debounceFn(newInputValue); // debounce the call the on change event
        }}
        renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
          <TextField {...params} value={textValue} label="Search for address" fullWidth />
        )}
        renderOption={(props, option) => {
          return (
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item>
                  <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
                </Grid>
                <Grid item xs>
                  {option.fullAddress}
                </Grid>
              </Grid>
            </li>
          );
        }}
      ></Autocomplete>
    </Box>
  );
};
